import { createLibrary, defineComponent } from "@openuidev/lang-core";
import { z } from "zod";
import { coreWidgets } from "./catalogue/core";
import { humanitiesWidgets } from "./catalogue/humanities";
import { mathWidgets } from "./catalogue/math";
import { reasoningWidgets } from "./catalogue/reasoning";
import { scienceWidgets } from "./catalogue/science";

// The tutor's widget library: the "component library" box of OpenUI's reference
// architecture, owned by us. Each widget is a name, a Zod schema for its props and a
// usage note; the schema is what the model sees and what validation enforces. Widgets
// are grouped by subject in ./catalogue; React renderers bind to the same names in
// `@tutor/ui/widgets`, so `component` is null here and this package runs in the Worker.
//
// Rules for widgets (ADR-0014, ADR-0031):
// - No answer keys in props: grading happens in the engine, never in the UI.
// - No free-form numbers about progress: mastery figures come from engine data ids.
// - No item content: a widget about an exercise references the engine's item by id
//   (`ChoiceQuestion`), and the server drops it unless that item is the current one.
// - Every widget is usable by keyboard and screen reader, in English and French.
// - Adding or changing a widget is a versioned release (the library is part of the
//   cached system prompt), not a prompt tweak.

/** Every widget a Stack may hold, in the order the system prompt lists them. */
export const contentWidgets = [
  ...coreWidgets,
  ...mathWidgets,
  ...reasoningWidgets,
  ...scienceWidgets,
  ...humanitiesWidgets,
] as const;

const Stack = defineComponent({
  name: "Stack",
  description: "Vertical layout root. Every widget block starts with one Stack.",
  props: z.object({
    children: z.array(z.union(contentWidgets.map((widget) => widget.ref))),
  }),
  component: null,
});

export const WIDGET_LIBRARY_VERSION = "0.5.0";

function build() {
  return createLibrary({
    id: `tutor-widgets@${WIDGET_LIBRARY_VERSION}`,
    root: "Stack",
    components: [Stack, ...contentWidgets],
  });
}

let library: ReturnType<typeof build> | undefined;

/**
 * The library, built on first use. `createLibrary` draws a random id, which Workers
 * forbid at module scope ("Disallowed operation called within global scope"), so the
 * library must not be built at import time.
 */
export function widgetLibrary(): ReturnType<typeof build> {
  library ??= build();
  return library;
}

export type WidgetName = keyof ReturnType<typeof build>["components"];
